import React, { useState } from "react";
import { Box, Chip, Popover, Slider, Stack, TextField } from "@mui/material";
import FilterIcon from "@mui/icons-material/FilterAlt";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

// A lower and an upper bound, either of which may be left open. Kept as text whatever the field
// holds, since that is what the inputs produce; the caller turns them into a filter.
export type Range = { min: string | null; max: string | null };

export const EMPTY_RANGE: Range = { min: null, max: null };

export const isRangeSet = (range: Range) =>
  range.min !== null || range.max !== null;

// A filter on a span of values, drawn as a QuickFilter chip: filled and reading "Price: 0 - 5 000"
// once a bound is set, outlined and reading just "Price" while neither is. Clicking it opens the
// span — two number fields, or a two-handled slider over `options` when the values are graded names
// in a fixed order rather than quantities, as clarity is.
function RangeFilter({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: Range;
  onChange: (value: Range) => void;
  options?: string[];
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // The bounds are edited on a draft and only handed over when the editor closes. Reporting every
  // tick would refire the search per pixel, and would resize the chip the editor is anchored to —
  // which slides the editor out from under the handle being dragged.
  const [draft, setDraft] = useState<Range>(value);
  const set = isRangeSet(value);

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setDraft(value);
    setAnchor(event.currentTarget);
  };

  const commit = () => {
    setAnchor(null);
    onChange(draft);
  };

  // The slider spans the whole list until someone narrows it, so it opens showing what is currently
  // included rather than an empty control.
  const gradeSpan: number[] = options
    ? [
        draft.min ? Math.max(options.indexOf(draft.min), 0) : 0,
        draft.max ? Math.max(options.indexOf(draft.max), 0) : options.length - 1,
      ]
    : [];

  const bounds = options ? (
    <Box sx={{ width: 260, px: 2, pt: 3 }}>
      <Slider
        value={gradeSpan}
        min={0}
        max={options.length - 1}
        step={1}
        marks
        valueLabelDisplay="on"
        valueLabelFormat={(index) => options[index]}
        onChange={(_, next) => {
          const [from, to] = next as number[];
          setDraft({
            min: from === 0 ? null : options[from],
            max: to === options.length - 1 ? null : options[to],
          });
        }}
      />
    </Box>
  ) : (
    (["min", "max"] as const).map((which) => (
      <TextField
        key={which}
        label={which === "min" ? "Min" : "Max"}
        type="number"
        size="small"
        sx={{ width: 130 }}
        value={draft[which] ?? ""}
        onChange={(event) =>
          setDraft({ ...draft, [which]: event.target.value || null })
        }
      />
    ))
  );

  return (
    <>
      <Chip
        label={
          !set
            ? label
            : options
              ? `${label}: ${value.min ?? options[0]} - ${value.max ?? options[options.length - 1]}`
              : `${label}: ${value.min ?? "…"} - ${value.max ?? "…"}`
        }
        icon={<FilterIcon fontSize="small" />}
        variant={set ? "filled" : "outlined"}
        clickable
        onClick={open}
        onDelete={set ? () => onChange(EMPTY_RANGE) : open}
        deleteIcon={set ? undefined : <ArrowDropDownIcon />}
      />
      <Popover
        anchorEl={anchor}
        open={anchor !== null}
        onClose={commit}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack direction="row" spacing={1} sx={{ p: 2 }}>
          {bounds}
        </Stack>
      </Popover>
    </>
  );
}

export default RangeFilter;
